import React, { useEffect, useState } from 'react';
import '../styles/pages/Players.scss';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InstagramIcon from '@mui/icons-material/Instagram';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SportsIcon from '@mui/icons-material/Sports';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import forTheParents from '../images/for-the-parents.jpeg';
import parentMockup1 from '../images/mockups/for-the-parents1.png';
import parentMockup2 from '../images/mockups/for-the-parents2.png';
import parentMockup3 from '../images/mockups/for-the-parents3.png';
import playsportLogo from '../images/partners/Playsport-logo.png';
import kickitoutLogo from '../images/partners/kick-it-out-logo.png';
import onlineTicketSellerLogo from '../images/partners/online-ticket-seller.png';
import parentImage1 from '../images/getimg_ai_img-JnKHszdvVhQW1idKC6F0q.jpeg';
import parentImage2 from '../images/getimg_ai_img-zSn3Isg63V0QYE6uPJbbY.jpeg';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Helmet } from 'react-helmet';
import { useJoinModal } from '../components/JoinModalProvider';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Custom arrow components for the slider
const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-prev-arrow`} onClick={onClick}>
      <ArrowBackIosNewIcon />
    </div>
  );
};

const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-next-arrow`} onClick={onClick}>
      <ArrowForwardIosIcon />
    </div>
  );
};

const Parents = () => {
  const { openModal } = useJoinModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [emailError, setEmailError] = useState('');

  // Parent slider ref
  const parentSliderRef = (slider) => {
    if (slider !== null) {
      window.parentSlider = slider;
    }
  };

  // Base settings for phone carousels
  const baseSliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    cssEase: 'ease-out',
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 20,
    waitForAnimate: false
  };

  // Email validation function
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value.trim();
    
    // Reset states
    setEmailError('');
    setSubmitStatus(null);

    // Validate email
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call to API would go here
      // For now, simulate success
      setTimeout(() => {
        setSubmitStatus('success');
        e.target.reset();
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error:', error);
      setIsSubmitting(false);
    }
  };

  // Smooth scroll function
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // State for FAQ accordion
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  // FAQ data
  const faqs = [
    {
      question: "How does FirstWhistle help me stay updated on my child's matches?",
      answer: "FirstWhistle sends automatic notifications about upcoming matches, cancellations, and venue changes. You'll receive all details like location, time, and kit requirements directly to your phone."
    },
    {
      question: "Can I track my child's performance and development?",
      answer: "Yes! You can view your child's match statistics, coach feedback, awards, and development notes all in one place. The app tracks progress over the season so you can celebrate their improvement."
    },
    {
      question: "How do payments work through FirstWhistle?",
      answer: "FirstWhistle simplifies payments through our integration with Online Ticket Seller, allowing cashless transactions for match fees, subscriptions, and team events. You'll receive clear notifications when payments are due and can pay securely through the app with just a few taps, eliminating the need for cash handovers or chasing payment reminders."
    },
    {
      question: "Can I communicate with coaches through the app?",
      answer: "Absolutely. FirstWhistle provides a direct messaging system between parents and coaches. You can discuss your child's development, ask questions, or coordinate logistics all in one place."
    },
    {
      question: "Is my child's data secure?",
      answer: "Security is our priority. All personal information is encrypted, and we never share data with third parties. Only authorised team officials and parents have access to player profiles."
    }
  ];

  // To ensure correct page title
  useEffect(() => {
    document.title = "FirstWhistle - Parents";
  }, []);

  return (
    <div className="players-page parents-page firstwhistle-parents-page">
      <Helmet>
        <title>FirstWhistle for Parents | Stay Connected with Your Child's Football Journey</title>
        <meta name="description" content="FirstWhistle helps parents stay connected with their child's football journey. Track matches, receive updates, and support your young player's development with our comprehensive parent portal." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/parents" />
        <meta property="og:title" content="FirstWhistle for Parents | Stay Connected with Your Child's Football Journey" />
        <meta property="og:description" content="FirstWhistle helps parents stay connected with their child's football journey. Track matches, receive updates, and support your young player's development with our comprehensive parent portal." />
        <meta property="og:image" content="https://firstwhistle.app/images/parents-hero-bg.jpeg" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://firstwhistle.app/parents" />
        <meta name="twitter:title" content="FirstWhistle for Parents | Stay Connected with Your Child's Football Journey" />
        <meta name="twitter:description" content="FirstWhistle helps parents stay connected with their child's football journey. Track matches, receive updates, and support your young player's development with our comprehensive parent portal." />
        <meta name="twitter:image" content="https://firstwhistle.app/images/parents-hero-bg.jpeg" />
        
        <link rel="canonical" href="https://firstwhistle.app/parents" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "FirstWhistle for Parents",
              "description": "FirstWhistle helps parents stay connected with their child's football journey. Track matches, receive updates, and support your young player's development with our comprehensive parent portal.",
              "mainEntity": {
                "@type": "SoftwareApplication",
                "name": "FirstWhistle",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "iOS, Android",
                "description": "Comprehensive football team management application for parents to stay connected with their child's football journey.",
                "offers": {
                  "@type": "Offer",
                  "availability": "PreOrder",
                  "price": "0",
                  "priceCurrency": "GBP"
                },
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Parents of Young Football Players",
                  "geographicArea": {
                    "@type": "AdministrativeArea",
                    "name": "United Kingdom"
                  }
                },
                "potentialAction": {
                  "@type": "JoinAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://firstwhistle.app/parents",
                    "actionPlatform": [
                      "http://schema.org/DesktopWebPlatform",
                      "http://schema.org/MobileWebPlatform"
                    ]
                  },
                  "name": "Sign Up"
                },
                "featureList": [
                  "Match schedule tracking",
                  "Performance updates",
                  "Team communication",
                  "Photo sharing",
                  "Parent portal access"
                ]
              }
            }
          `}
        </script>
      </Helmet>
      
      {/* <header>
        <svg 
          className="bokeh" 
          viewBox="0 0 10 10" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="10%" cy="85%" r="75%"  />
          <circle cx="45%" cy="50%" r="15%"   />
          <circle cx="85%" cy="35%" r="30%"  />
          <circle cx="60%" cy="85%" r="20%"   />
          <circle cx="45%" cy="50%" r="30%"   />
          <circle cx="35%" cy="25%" r="20%"   />
          <circle cx="90%" cy="-25%" r="35%"  />
          <circle cx="-15%" cy="30%" r="30%"  />
          <circle cx="65%" cy="85%" r="55%"   />
          <circle cx="45%" cy="50%" r="20%"   />
        </svg>
      </header> */}
      
      {/* 1. Hero Section */}
      <section className="hero-section parents-hero-section">
        <div className="hero-content parents-hero-content">
          <h1 className="parents-page-h1"><span className="page-identifier">Parents:</span> Keep Up With Your Child's Football Journey.</h1>
          <p className="subtext parents-page-subtext">FirstWhistle connects you to every match, milestone and memory in your child's football development.</p>
          <div className="cta-buttons">
            <button className="cta-button primary" onClick={openModal}>Join Now</button>
            <button 
              className="cta-button secondary"
              onClick={() => scrollToSection('how-it-works')}
            >
              See How It Works
            </button>
          </div>
        </div>
        <div className="hero-background">
          <img src={forTheParents} alt="For the Parents" className="parents-hero-image" />
          <div className="overlay"></div>
        </div>
      </section>

      {/* 2. Why It Matters Section */}
      <section className="why-matters-section" id="how-it-works">
        <h2>Making grassroots football easier for busy parents.</h2>
        <div className="content-blocks">
          <div className="content-block">
            <div className="icon-container">
              <NotificationsActiveIcon className="icon" />
            </div>
            <h3>Never Miss a Match</h3>
            <p>Fixture alerts, directions to venues, and last-minute changes delivered straight to your phone.</p>
          </div>
          <div className="content-block">
            <div className="icon-container">
              <EmojiEventsIcon className="icon" />
            </div>
            <h3>Celebrate Every Achievement</h3>
            <p>See when your child wins awards, scores goals, or makes the team of the week – with photos and highlights.</p>
          </div>
          <div className="content-block">
            <div className="icon-container">
              <FamilyRestroomIcon className="icon" />
            </div>
            <h3>Stay Updated on Team Information</h3>
            <p>Receive important team announcements and updates through the FirstWhistle platform to keep you informed about your child's team.</p>
          </div>
        </div>
      </section>

      {/* 4. Image Columns Section */}
      <section className="image-columns-section">
        <div className="image-columns" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '2rem',
          margin: '4rem auto',
          maxWidth: '1200px',
          padding: '0 2rem'
        }}>
          <div className="image-column" style={{
            borderRadius: '12px',
            overflow: 'hidden',
            aspectRatio: '16/9',
            position: 'relative'
          }}>
            <img 
              src={parentImage1} 
              alt="Parent supporting child in football" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '12px'
              }}
            />
          </div>
          <div className="image-column" style={{
            borderRadius: '12px',
            overflow: 'hidden',
            aspectRatio: '16/9',
            position: 'relative'
          }}>
            <img 
              src={parentImage2} 
              alt="Parent and child football moment" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '12px'
              }}
            />
          </div>
        </div>
      </section>

      {/* 4. Screenshots Section */}
      <section className="screenshots-section" id="screenshots-section">
        <h2>Everything you need to support your child's football journey</h2>
        <div className="audience-section">
          <div className="audience-columns" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="audience-column">
              <div className="phone-carousel">
                <Slider ref={parentSliderRef} {...baseSliderSettings} key="parent-slider-1">
                  <div className="phone-slide">
                    <img src={parentMockup1} alt="FirstWhistle for Parents - Fixtures" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={parentMockup2} alt="FirstWhistle for Parents - Payments" className="audience-phone" draggable="false" />
                  </div>
                </Slider>
              </div>
              <h3>Match Day <span style={{ color: '#ffd700' }}>Updates</span></h3>
              <p>Never miss a match with instant notifications about fixtures, venues, and last-minute changes.</p>
              <ul className="feature-list">
                <li><CheckCircleOutlineIcon className="check-icon" /> Real-time match notifications</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> One-tap directions to venues</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Weather updates and kit requirements</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Team announcements and news</li>
              </ul>
            </div>
            <div className="audience-column">
              <div className="phone-carousel">
                <Slider ref={parentSliderRef} {...baseSliderSettings} key="parent-slider-2">
                  <div className="phone-slide">
                    <img src={parentMockup2} alt="FirstWhistle for Parents - Development" className="audience-phone" draggable="false" />
                  </div>
                  <div className="phone-slide">
                    <img src={parentMockup3} alt="FirstWhistle for Parents - Progress" className="audience-phone" draggable="false" />
                  </div>
                </Slider>
              </div>
              <h3>Player <span style={{ color: '#ffd700' }}>Development</span></h3>
              <p>Track your child's progress and celebrate their achievements throughout the season.</p>
              <ul className="feature-list">
                <li><CheckCircleOutlineIcon className="check-icon" /> Performance insights and feedback</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Achievement milestones and awards</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Match reports and highlights</li>
                <li><CheckCircleOutlineIcon className="check-icon" /> Training schedule updates</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. FAQ Section */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          <div className="faq-item">
            <div 
              className="faq-question" 
              onClick={() => toggleFaq(0)}
            >
              <h3>My child's club is not on the app, how do I get them involved?</h3>
              {expandedFaq === 0 ? <ExpandLessIcon className="expand-icon" /> : <ExpandMoreIcon className="expand-icon" />}
            </div>
            <div className={`faq-answer ${expandedFaq === 0 ? 'expanded' : ''}`}>
              <p>Simply send us the club's contact details and we'll reach out to them. The more parents who ask, the greater the chance they'll join. We're working with many clubs already and are expanding rapidly.</p>
            </div>
          </div>
          {faqs.map((faq, index) => (
            <div className="faq-item" key={index+1}>
              <div 
                className="faq-question" 
                onClick={() => toggleFaq(index+1)}
              >
                <h3>{faq.question}</h3>
                {expandedFaq === index+1 ? <ExpandLessIcon className="expand-icon" /> : <ExpandMoreIcon className="expand-icon" />}
              </div>
              <div className={`faq-answer ${expandedFaq === index+1 ? 'expanded' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners Section */}
      <div className="partners">
        <div className="partner-logos">
          <a href="https://playsport.co.uk" target="_blank" rel="noopener noreferrer"><img src={playsportLogo} alt="Playsport" /></a>
          <a href="https://kickitout.co.uk" target="_blank" rel="noopener noreferrer"><img src={kickitoutLogo} alt="Kick it out" /></a>
          <a href="https://www.onlineticketseller.com/" target="_blank" rel="noopener noreferrer"><img src={onlineTicketSellerLogo} alt="Online Ticket Seller" /></a>
        </div>
      </div>

      {/* 6. Social Proof Section */}
      <section className="social-proof-section">
        <h2>What parents are saying about FirstWhistle</h2>
        <div className="testimonials">
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"I've never missed a match since we started using FirstWhistle. The notifications and one-tap directions to away venues are absolute lifesavers for busy parents."</p>
            <div className="author">- Sarah, Parent of U12 player</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"Being able to see my son's team updates has been fantastic. I now understand what my son is working on in training and can be a better cheerleader for his achievements."</p>
            <div className="author">- James, Parent of U10 player</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"The integration with Online Ticket Seller makes team fees so simple. No more awkward cash handovers or forgotten payment reminders!"</p>
            <div className="author">- Michelle, Parent of U14 player</div>
          </div>
        </div>
      </section>

      {/* 7. Call to Action Section */}
      <section className="cta-section">
        <h2>Don't miss your child's football moments.</h2>
        <p>Be the first to experience FirstWhistle when we launch and make grassroots football simpler for your family.</p>
        <div className="cta-buttons">
          <button className="cta-button primary" onClick={openModal}>Join Now</button>
          <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
            <InstagramIcon /> Follow Us on Instagram
          </a>
        </div>
      </section>

      {/* 8. Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <p className="tagline">FirstWhistle – Where every player counts.</p>
          </div>
          <div className="footer-navigation">
            <div className="footer-links">
              <Link to="/about">About</Link>
              <Link to="/players">Players</Link>
              <Link to="/coaches">Coaches</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                <InstagramIcon className="social-icon" />
              </a>
              <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                <FacebookIcon className="social-icon" />
              </a>
            </div>
          </div>
          <div className="footer-legal">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Parents; 